// DiagramRenderer — programmatic visual_spec → branded SVG rendering.
// Replaces the OpenAI image-generation pipeline for structured diagrams.
// Renders:
//
//   • flowchart      → React Flow + dagre top-down layout
//   • concept_map    → React Flow + radial layout (gold-ringed central node)
//   • hierarchy      → React Flow + dagre tree layout
//   • timeline       → custom horizontal SVG
//   • comparison_table → HTML table
//   • cycle_diagram  → custom radial SVG
//
// React Flow + custom node components is lazy-loaded so the library only
// ships when a learner actually opens a visual page. Layout algorithms
// (dagre / radial) run in pure JS, so diagrams render instantly with
// no auto-layout surprises and pixel-perfect typography.

import { lazy, Suspense, type ReactNode } from 'react';
import { motion } from 'framer-motion';

interface VisualSpec {
  type: string;
  data?: Record<string, unknown>;
  caption?: string;
  /** Server-pre-rendered SVG string. When present, we paint it directly
   *  via dangerouslySetInnerHTML — no layout, no React Flow chunk. */
  svgCached?: string;
}

interface Props {
  visualSpec: VisualSpec;
}

const ReactFlowDiagram = lazy(() =>
  import('./ReactFlowDiagram').then((m) => ({ default: m.ReactFlowDiagram })),
);

// ── Cycle diagram (custom SVG) ──────────────────────────────────────────

function CycleDiagram({ data }: { data: Record<string, unknown> }) {
  const steps = (data.steps as Array<Record<string, unknown>>) ?? [];
  if (steps.length === 0) return null;
  const size = 380;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 80;
  const angleStep = (2 * Math.PI) / steps.length;

  return (
    <div className="flex flex-col items-center gap-4">
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-[400px] h-auto">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#A594F9" strokeOpacity="0.35" strokeDasharray="3 4" strokeWidth="1" />
        {steps.map((step, i) => {
          const angle = i * angleStep - Math.PI / 2;
          const nextAngle = (i + 1) * angleStep - Math.PI / 2;
          const x = cx + r * Math.cos(angle);
          const y = cy + r * Math.sin(angle);
          const arrowEndX = cx + r * Math.cos(nextAngle - 0.18);
          const arrowEndY = cy + r * Math.sin(nextAngle - 0.18);
          const label = String(step.label ?? '').slice(0, 24);
          return (
            <g key={i}>
              <line x1={x} y1={y} x2={arrowEndX} y2={arrowEndY} stroke="#A594F9" strokeWidth="2" markerEnd="url(#arrow)" />
              <circle cx={x} cy={y} r="32" fill="#1B2240" stroke="#6C5CE7" strokeWidth="2" />
              <text x={x} y={y} textAnchor="middle" dominantBaseline="middle" fill="#FFFFFF" fontSize="11" fontFamily="Plus Jakarta Sans, system-ui, sans-serif" fontWeight="600">
                {label}
              </text>
            </g>
          );
        })}
        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#A594F9" />
          </marker>
        </defs>
      </svg>
      {steps.length > 0 && steps[0].description ? (
        <ol className="text-sm font-body text-content-secondary space-y-2 list-decimal pl-5 max-w-md">
          {steps.map((s, i) => (
            <li key={i}>
              <span className="font-semibold text-white">{String(s.label ?? '')}</span>
              {s.description ? <span className="text-white/70"> — {String(s.description)}</span> : null}
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}

// ── Comparison table (HTML) ─────────────────────────────────────────────

function ComparisonTable({ data }: { data: Record<string, unknown> }) {
  const columns = (data.columns as string[]) ?? [];
  const rows = (data.rows as Array<{ label: string; values: string[] }>) ?? [];
  return (
    <div className="overflow-x-auto rounded-lg border border-white/10">
      <table className="w-full text-sm font-body border-collapse">
        <thead>
          <tr className="bg-white/5">
            <th className="px-3 py-2 text-left text-white/80 font-semibold border-b border-white/10">&nbsp;</th>
            {columns.map((c, i) => (
              <th key={i} className="px-3 py-2 text-left text-white font-semibold border-b border-white/10">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className={ri % 2 === 0 ? '' : 'bg-white/[0.02]'}>
              <td className="px-3 py-2 text-white/90 font-semibold border-b border-white/5">{row.label}</td>
              {(row.values ?? []).map((v, ci) => (
                <td key={ci} className="px-3 py-2 text-white/70 border-b border-white/5">{v}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Timeline (custom horizontal SVG) ────────────────────────────────────

function Timeline({ data }: { data: Record<string, unknown> }) {
  const events = (data.events as Array<Record<string, unknown>>) ?? [];
  if (events.length === 0) return null;
  return (
    <div className="relative py-6">
      {/* Spine */}
      <div
        aria-hidden
        className="absolute left-3 top-6 bottom-6 w-px"
        style={{ background: 'linear-gradient(to bottom, #6C5CE7, #F0A500)' }}
      />
      <ol className="space-y-5 ml-0">
        {events.map((e, i) => (
          <li key={i} className="relative pl-10">
            <span
              aria-hidden
              className="absolute left-0 top-1 w-6 h-6 rounded-full flex items-center justify-center"
              style={{
                background: 'radial-gradient(circle at 30% 30%, #8579F0, #4F40C7 80%)',
                border: '2px solid #F0A500',
                boxShadow: '0 0 0 3px rgba(240,165,0,0.18), 0 4px 10px -2px rgba(108,92,231,0.45)',
              }}
            />
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.14em] text-gold-400 mb-0.5">
                {String(e.date ?? '')}
              </div>
              <div className="font-heading font-semibold text-white text-base mb-1">
                {String(e.title ?? '')}
              </div>
              {e.description ? (
                <p className="text-sm font-body text-white/75 leading-relaxed m-0">
                  {String(e.description)}
                </p>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────

export function DiagramRenderer({ visualSpec }: Props) {
  const data = visualSpec.data ?? {};
  const type = visualSpec.type;

  let body: ReactNode;
  // Cache hit: paint the server-rendered SVG instantly. No JS layout,
  // no React Flow chunk download. This is the fast path for any
  // visual_spec generated after the server-side renderer landed.
  if (visualSpec.svgCached) {
    body = (
      <div
        className="diagram-cached w-full overflow-x-auto"
        // svgCached is generated server-side from a controlled template —
        // not user-injected HTML.
        dangerouslySetInnerHTML={{ __html: visualSpec.svgCached }}
      />
    );
  } else if (type === 'comparison_table') {
    body = <ComparisonTable data={data} />;
  } else if (type === 'cycle_diagram') {
    body = <CycleDiagram data={data} />;
  } else if (type === 'timeline') {
    body = <Timeline data={data} />;
  } else if (type === 'concept_map' || type === 'flowchart' || type === 'hierarchy') {
    // Fallback for legacy content saved before the server SVG cache existed.
    body = (
      <Suspense
        fallback={
          <div className="flex items-center justify-center py-10 text-white/60 text-sm font-body">
            Rendering diagram…
          </div>
        }
      >
        <ReactFlowDiagram visualSpec={visualSpec} />
      </Suspense>
    );
  } else {
    body = (
      <div className="text-xs text-white/60 italic py-4 text-center">
        Diagram type "{type}" not supported yet.
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="w-full"
    >
      {body}
    </motion.div>
  );
}
